import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Textarea,
} from '@goodday-clone/ui'
import {
  BadgeDollarSign,
  Building2,
  Mail,
  Phone,
  Layers,
  Percent,
  User,
  Calendar,
  FileText,
  Plus,
  Rocket,
  ArrowRight,
  CheckCircle2,
  RefreshCw,
  Sparkles,
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { api } from '@/api/client'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { DealDetailData } from './deal-detail-modal'
import { useAuth } from '@/context/AuthContext'
import { useDealSpaceAccess } from '@/shared/hooks/useDealSpaceAccess'
import { useDealStages } from '@/shared/hooks/useCrmVocabulary'
import { ServicesPicker } from './ServicesPicker'
import { PlaceOfSupplyField } from './PlaceOfSupplyField'
import { resolvePlaceOfSupply } from '@jira-clone/shared-types'
import { useProductServices, ProductService } from '@/shared/hooks/useProductServices'
import { createProjectFromDeal, formatDealTitle } from '@/lib/deal-project-utils'

interface AssignableUser {
  id: string
  name: string
  department?: string
  departmentRole?: string
}

interface AddDealModalProps {
  isOpen: boolean
  onClose: () => void
  onDealCreated: (newDeal: DealDetailData) => void
  initialStage?: string
}

const SOURCE_OPTIONS = [
  'Website Inbound',
  'Referral',
  'Partner Channel',
  'Trade Show',
  'Outbound Campaign',
  'Existing Client Expansion',
  'Other',
]

const PRIORITY_OPTIONS = [
  'Highest',
  'High',
  'Normal',
  'Low',
  'Lowest',
]

export function AddDealModal({
  isOpen,
  onClose,
  onDealCreated,
  initialStage = 'Discovery',
}: AddDealModalProps) {
  const navigate = useNavigate()
  const { user: currentUser, organization } = useAuth()
  const { canCreateSpace } = useDealSpaceAccess()
  const { data: dealStages = [] } = useDealStages()
  const { data: masterServices = [] } = useProductServices({ isActive: true })
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([])

  useEffect(() => {
    if (!isOpen) return
    api.get<{ data: AssignableUser[] }>('/users/assignable?for=leads')
      .then((res) => setAssignableUsers(res.data || []))
      .catch(() => setAssignableUsers([]))
  }, [isOpen])

  const [title, setTitle] = useState('')
  const [client, setClient] = useState('')
  const [contactPerson, setContactPerson] = useState('')
  const [contactDesignation, setContactDesignation] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [clientAddress, setClientAddress] = useState('')
  const [clientGstin, setClientGstin] = useState('')
  const [clientState, setClientState] = useState('')
  const [stage, setStage] = useState(initialStage)
  const [priority, setPriority] = useState('Normal')
  const [value, setValue] = useState('')
  const [probability, setProbability] = useState('50')
  const [assignedToUserId, setAssignedToUserId] = useState(currentUser?.id || '')
  const [closeDate, setCloseDate] = useState('Oct 15, 2026')
  const [source, setSource] = useState('Website Inbound')
  const [notes, setNotes] = useState('')
  const [services, setServices] = useState<string[]>([])
  const [createSpaceImmediately, setCreateSpaceImmediately] = useState(false)
  const [createQuotationImmediately, setCreateQuotationImmediately] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittingAction, setSubmittingAction] = useState<'create' | 'create_quotation' | 'create_space' | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Master-data service lookup (name → defaultRate)
  const masterDataByName = useMemo(() => {
    const map: Record<string, ProductService> = {}
    for (const ps of masterServices) {
      if (ps.name) map[ps.name.toLowerCase()] = ps
    }
    return map
  }, [masterServices])

  // Auto-calculated total from selected master data services
  const totalFromServices = useMemo(() => {
    if (services.length === 0) return 0
    return services.reduce((sum, svc) => {
      const md = masterDataByName[svc.toLowerCase()]
      return sum + (md?.defaultRate || 0)
    }, 0)
  }, [services, masterDataByName])

  // Track if user manually edited the value field
  const dealValueInputRef = useRef<HTMLInputElement>(null)
  const userModifiedDealValue = useRef(false)

  const handleDealValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    userModifiedDealValue.current = true
  }

  // Auto-populate value from master data services when they change (respects manual override)
  useEffect(() => {
    if (services.length > 0 && totalFromServices > 0 && !userModifiedDealValue.current) {
      setValue(String(totalFromServices))
    }
  }, [totalFromServices, services.length])

  // Win probability follows the stage's default (Discovery 20%, Demo & Validation
  // 40%, Proposal 60%, Negotiation 80%, Closed Won 100%), so a new deal starts at
  // its stage's default and switching stages before saving updates it.
  useEffect(() => {
    const stageDefault = dealStages.find((s) => s.name.toLowerCase() === stage?.toLowerCase())?.defaultProbability
    if (typeof stageDefault === 'number') {
      setProbability(String(stageDefault))
    }
  }, [stage, dealStages])

  const handleSubmit = async (
    e?: React.FormEvent,
    action: 'create' | 'create_quotation' | 'create_space' = 'create'
  ) => {
    if (e) e.preventDefault()
    if (!title.trim() || !client.trim()) {
      setError('Deal Title and Client Account are required')
      return
    }

    setIsSubmitting(true)
    setSubmittingAction(action)
    setError(null)

    const dealState = resolvePlaceOfSupply({ customerGstin: clientGstin, customerState: clientState }).label
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, '')) || totalFromServices || 0
    const numericProb = parseInt(probability, 10) || 50
    const ownerName = assignableUsers.find((u) => u.id === assignedToUserId)?.name || currentUser?.name || ''

    try {
      const created = await api.post<any>('/deals', {
        title: formatDealTitle(title, client),
        client: client.trim(),
        stage,
        priority,
        value: numericValue,
        probability: numericProb,
        assignedToUserId: assignedToUserId || undefined,
        source: source || undefined,
        closeDate: closeDate.trim() || undefined,
        notes: notes.trim() || undefined,
        services,
        contactPerson: contactPerson.trim() || undefined,
        contactDesignation: contactDesignation.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        address: clientAddress.trim() || undefined,
        billingAddress: clientAddress.trim() || undefined,
        clientAddress: clientAddress.trim() || undefined,
        gstin: clientGstin.trim() || undefined,
        clientGstin: clientGstin.trim() || undefined,
        clientState: dealState || undefined,
      }, { skipErrorToast: true })

      const dealId = created?._id || created?.id || `deal-${Date.now()}`
      const dealDetailData: DealDetailData = {
        id: dealId,
        title: created?.title || formatDealTitle(title, client),
        client: created?.client || client.trim(),
        stage: created?.stage || stage,
        priority: created?.priority || priority,
        value: created?.value ?? numericValue,
        probability: created?.probability ?? numericProb,
        owner: created?.owner || ownerName || null,
        closeDate: created?.closeDate || closeDate.trim() || null,
        notes: created?.notes || notes.trim() || null,
        services,
        organizationId: created?.organizationId || organization?.id || '',
        createdAt: created?.createdAt || new Date().toISOString(),
        updatedAt: created?.updatedAt || new Date().toISOString(),
        contactPerson: contactPerson.trim() || undefined,
        contactDesignation: contactDesignation.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        address: clientAddress.trim() || undefined,
        billingAddress: clientAddress.trim() || undefined,
        clientAddress: clientAddress.trim() || undefined,
        gstin: clientGstin.trim() || undefined,
        clientGstin: clientGstin.trim() || undefined,
        clientState: dealState || undefined,
        source: created?.source || source,
      }

      onDealCreated(dealDetailData)
      toast.success(`Deal "${dealDetailData.title}" created successfully!`)

      // Check if project should be created in Spaces
      let createdProjectId: string | null = null
      const shouldCreateSpace = canCreateSpace && (action === 'create_space' || createSpaceImmediately)
      if (shouldCreateSpace) {
        try {
          const res = await createProjectFromDeal(
            {
              id: dealId,
              title: dealDetailData.title,
              client: dealDetailData.client,
              notes: dealDetailData.notes,
              value: dealDetailData.value,
              owner: dealDetailData.owner,
              closeDate: dealDetailData.closeDate,
              contactPerson: dealDetailData.contactPerson,
              contactEmail: dealDetailData.contactEmail,
              contactPhone: dealDetailData.contactPhone,
              probability: dealDetailData.probability,
              services: dealDetailData.services,
              clientAddress: dealDetailData.clientAddress,
              clientGstin: dealDetailData.clientGstin,
            },
            organization?.id
          )

          if (res.success && res.project) {
            createdProjectId = res.project.id
            toast.success(`Space Project "${res.project.name}" [${res.project.key}] created successfully!`)
          } else if (res.error) {
            toast.error(`Deal created, but the space wasn't: ${res.error}`)
          }
        } catch (projErr) {
          console.error('Failed to auto-create space project:', projErr)
          toast.error("Deal created, but the space couldn't be created.")
        }
      }

      // Check if quotation generator should be opened
      const shouldCreateQuotation = action === 'create_quotation' || createQuotationImmediately
      if (shouldCreateQuotation) {
        const quotationPayload = {
          dealId: dealId,
          title: dealDetailData.title,
          clientName: dealDetailData.client,
          clientAddress: clientAddress.trim(),
          clientGstin: clientGstin.trim(),
          clientState: dealState,
          contactPerson: contactPerson.trim(),
          contactDesignation: contactDesignation.trim(),
          email: contactEmail.trim(),
          phone: contactPhone.trim(),
          value: numericValue,
          notes: notes.trim(),
          description: notes.trim(),
          services,
          owner: ownerName,
          closeDate: closeDate.trim(),
          stage: dealDetailData.stage,
        }

        const searchParams = new URLSearchParams({
          new: 'quotation',
          dealId: dealId,
          clientName: dealDetailData.client,
          title: dealDetailData.title,
          contactPerson: contactPerson.trim(),
          contactDesignation: contactDesignation.trim(),
          email: contactEmail.trim(),
          phone: contactPhone.trim(),
          value: String(numericValue),
        })
        if (clientAddress.trim()) searchParams.set('clientAddress', clientAddress.trim())
        if (clientGstin.trim()) searchParams.set('clientGstin', clientGstin.trim())
        if (dealState) searchParams.set('clientState', dealState)
        if (services.length > 0) {
          searchParams.set('services', services.join(','))
        }

        onClose()
        navigate(`/app/sales/quotations?${searchParams.toString()}`, {
          state: { fromDeal: quotationPayload },
        })
        return
      }

      if (action === 'create_space' && createdProjectId) {
        onClose()
        navigate(`/spaces/${createdProjectId}`)
        return
      }

      onClose()

      // Reset form
      setTitle('')
      setClient('')
      setContactPerson('')
      setContactEmail('')
      setContactPhone('')
      setClientAddress('')
      setClientGstin('')
      setValue('')
      setProbability('50')
      setNotes('')
      setServices([])
      setCreateSpaceImmediately(false)
      setCreateQuotationImmediately(false)
      userModifiedDealValue.current = false
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'create deals'))
    } finally {
      setIsSubmitting(false)
      setSubmittingAction(null)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl bg-card border border-border p-6 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b border-border/60">
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-foreground">
            <span className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600">
              <BadgeDollarSign className="h-5 w-5" />
            </span>
            Add New Sales Deal
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Create an active sales opportunity with full commercial and delivery capabilities
          </p>
        </DialogHeader>

        {error && (
          <div className="p-3 text-xs bg-destructive/10 text-destructive border border-destructive/20 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={(e) => handleSubmit(e, 'create')} className="space-y-4 pt-2">
          {/* Row 1: Title & Client */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Deal Title *</Label>
              <Input
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Acme Enterprise Expansion"
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Client / Account *</Label>
              <Input
                required
                value={client}
                onChange={(e) => setClient(e.target.value)}
                placeholder="e.g. Acme Systems"
                className="h-9 text-xs"
              />
            </div>
          </div>

          {/* Row 2: Contact Person & Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Primary Contact Name</Label>
              <Input
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
                placeholder="e.g. Sarah Jenkins"
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Designation</Label>
              <Input
                value={contactDesignation}
                onChange={(e) => setContactDesignation(e.target.value)}
                placeholder="e.g. CTO, Procurement Lead"
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Contact Email</Label>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="e.g. sarah@acme.com"
                className="h-9 text-xs"
              />
            </div>
          </div>

          {/* Row 3: Phone, Stage, Priority */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Contact Phone</Label>
              <Input
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="e.g. +1 (555) 019-2834"
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Sales Stage</Label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {dealStages.filter((st) => !st.isWon).map((st) => (
                  <option key={st.id} value={st.name}>
                    {st.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Priority</Label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 3.5: Client Address & GSTIN */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Client Address / Location</Label>
              <Input
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
                placeholder="e.g. 401 Cyber Towers, Magarpatta, Pune"
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Client GSTIN</Label>
              <Input
                value={clientGstin}
                onChange={(e) => setClientGstin(e.target.value.toUpperCase())}
                placeholder="e.g. 27AALCD3566E1ZJ"
                className="h-9 text-xs uppercase"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <PlaceOfSupplyField
                id="deal-client-state"
                label="Customer State"
                customerGstin={clientGstin}
                state={clientState}
                onChange={setClientState}
                showSupplyHint={false}
                inputClassName="h-9 w-full rounded-md border border-input bg-background px-3 text-xs"
                labelClassName="text-xs font-semibold block mb-1.5"
              />
            </div>
          </div>

          {/* Row 4: Deal Value, Probability, Close Date */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Total (₹)</Label>
              <Input
                ref={dealValueInputRef}
                type="number"
                value={value}
                onChange={handleDealValueChange}
                onFocus={() => userModifiedDealValue.current = true}
                placeholder={totalFromServices > 0 ? `Auto: ${totalFromServices}` : "75000"}
                className="h-9 text-xs"
              />
              {totalFromServices > 0 && (
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400">
                  Auto-calculated from {services.length} selected service{services.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Win Probability (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={probability}
                onChange={(e) => setProbability(e.target.value)}
                placeholder="80"
                className="h-9 text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Target Close Date</Label>
              <Input
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                placeholder="Oct 15, 2026"
                className="h-9 text-xs"
              />
            </div>
          </div>

          {/* Row 5: Owner, Source */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Deal Owner</Label>
              <select
                value={assignedToUserId}
                onChange={(e) => setAssignedToUserId(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {assignableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}{u.departmentRole ? ` — ${u.departmentRole}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Deal Source</Label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {SOURCE_OPTIONS.map((src) => (
                  <option key={src} value={src}>
                    {src}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 6: Notes */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Deal Description / Scope Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter proposal terms, client requirements, milestones, or next steps..."
              rows={3}
              className="text-xs"
            />
          </div>

          {/* Row 7: Services Section */}
          <div className="pt-2 border-t border-border/60">
            <ServicesPicker
              selectedServices={services}
              onChange={setServices}
              label="Services"
              description="Select company services included in this deal. Total is auto-calculated from master data rates."
              showRates={true}
              masterData={masterServices}
            />
          </div>

          {/* Row 8: Fast-Track Actions (Quotation & Spaces Kickoff) */}
          <div className="p-3.5 rounded-xl bg-muted/40 border border-border/70 space-y-2.5">
            <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
              <Sparkles className="h-4 w-4 text-emerald-500" />
              <span>Deal Automation & Project Workflow</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <label className="flex items-start gap-2.5 p-2 rounded-lg border border-border/60 bg-card hover:bg-muted/50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={createQuotationImmediately}
                  onChange={(e) => setCreateQuotationImmediately(e.target.checked)}
                  className="mt-0.5 rounded border-input text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="font-semibold text-foreground flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5 text-indigo-600" /> Create Quotation
                  </span>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Open quotation generator with this deal&apos;s client, contact, and services prefilled.
                  </p>
                </div>
              </label>

              {canCreateSpace && (
              <label className="flex items-start gap-2.5 p-2 rounded-lg border border-border/60 bg-card hover:bg-muted/50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={createSpaceImmediately}
                  onChange={(e) => setCreateSpaceImmediately(e.target.checked)}
                  className="mt-0.5 rounded border-input text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <span className="font-semibold text-foreground flex items-center gap-1.5">
                    <Rocket className="h-3.5 w-3.5 text-emerald-600" /> Create Space Project
                  </span>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Instantly deploy an active delivery space and Kanban board at /spaces for project execution.
                  </p>
                </div>
              </label>
              )}
            </div>
          </div>

          <DialogFooter className="pt-3 border-t border-border/60 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              className="text-xs"
            >
              Cancel
            </Button>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isSubmitting}
                onClick={(e) => handleSubmit(e, 'create_quotation')}
                className="text-xs gap-1.5 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 shadow-2xs"
              >
                {isSubmitting && submittingAction === 'create_quotation' ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileText className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                )}
                <span>Add &amp; Create Quotation</span>
              </Button>

              {canCreateSpace && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isSubmitting}
                onClick={(e) => handleSubmit(e, 'create_space')}
                className="text-xs gap-1.5 border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 shadow-2xs"
              >
                {isSubmitting && submittingAction === 'create_space' ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Rocket className="h-3.5 w-3.5 text-emerald-600" />
                )}
                <span>Add &amp; Create Space</span>
              </Button>
              )}

              <Button
                type="submit"
                size="sm"
                disabled={isSubmitting}
                className="text-xs gap-1.5 bg-primary text-primary-foreground shadow-sm"
              >
                {isSubmitting && submittingAction === 'create' ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
                <span>Add Deal</span>
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
